import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import {
  Flex,
  Box,
  useDisclosure,
  Popover,
  PopoverTrigger,
  Tag,
  TagLeftIcon,
  useOutsideClick,
  PopoverContent,
  PopoverBody,
  TagRightIcon,
} from '@chakra-ui/react';
import { FilterLines } from '@kula-ai/icons/outline/FilterLines';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { FilterList } from './UiItems';
import { SecondaryFilter } from './SecondaryFilter';
import { KText } from '@/ui-library/components/text';

export const MainFilter = memo(
  ({
    configs = [],
    activeFilters = {},
    filterActions = () => {},
    mainFilterProps,
  }) => {
    const [showClear, setShowClear] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('');
    const [disclosureState, setDisclosureState] = useState({});

    const createModalActions = useCallback(
      (id) => ({
        onOpen: () => setDisclosureState((prev) => ({ ...prev, [id]: true })),
        onClose: () => setDisclosureState((prev) => ({ ...prev, [id]: false })),
        onToggle: () =>
          setDisclosureState((prev) => ({ ...prev, [id]: !prev[id] })),
      }),
      []
    );

    const filtersConfig = useMemo(
      () =>
        (Array.isArray(configs) ? configs : []).map((item) => ({
          ...item,
          modalActions: {
            ...createModalActions(item.id),
            isOpen: !!disclosureState[item.id],
          },
        })),
      [configs, createModalActions, disclosureState]
    );
    const ref = useRef(null);
    const { isOpen, onClose, onToggle } = useDisclosure();

    useOutsideClick({
      ref: ref,
      handler: () => {
        setSelectedFilter('');
        onClose();
      },
    });

    useEffect(() => {
      const isSomeFiltersApplied = filtersConfig?.some(
        (filter) => activeFilters?.[filter?.filterKey]?.length
      );
      setShowClear(!!isSomeFiltersApplied);
    }, [activeFilters, filtersConfig]);

    const isFilterSelected = (filterId, activeFilters) => {
      const res = activeFilters?.[filterId]?.length > 0;
      return res;
    };

    const handleFilterListOnclick = (selected) => {
      const { id, modalActions } = selected || {};
      const isFilterActive = isFilterSelected(id, activeFilters);
      if (isFilterActive) {
        if (modalActions) modalActions.onOpen();
        onClose();
      } else setSelectedFilter((prev) => (prev?.id === id ? prev : selected));
    };

    const clearSelected = () => {
      setSelectedFilter('');
    };

    const clearSelectedForSecondaryFilter = () => {
      setSelectedFilter('');
      onClose();
    };

    const renderTrigger = () => {
      const currentFilterId = selectedFilter?.id;
      return (
        <Popover
          placement="bottom-start"
          isOpen={isOpen}
          closeOnBlur={false}
          modifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['auto'],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ]}
        >
          <PopoverTrigger>
            <Tag
              height="34px"
              cursor="pointer"
              bg="primary-50"
              onClick={onToggle}
              px="sm"
              borderRadius="xs"
              gap="6px"
            >
              <TagLeftIcon
                as={FilterLines}
                set="bold"
                width="16px"
                height="16px"
                color="primary-600"
                mr="0"
              />
              <KText size="sm-medium" color="primary-600">
                Filters
              </KText>
              <TagRightIcon
                as={ChevronDown}
                set="bold"
                color="primary-600"
                ml="0"
              />
            </Tag>
          </PopoverTrigger>
          <PopoverContent
            w={
              currentFilterId
                ? ' 350px'
                : mainFilterProps?.popoverContentProps?.w || '200px'
            }
            maxH="500px"
            overflow="auto"
          >
            <PopoverBody px="12px">
              {!currentFilterId ? (
                <Flex flexDir="column" gap=".5em">
                  <FilterList
                    filtersConfig={filtersConfig}
                    handleFilterListOnclick={handleFilterListOnclick}
                  />
                </Flex>
              ) : (
                <SecondaryFilter
                  filter={filtersConfig.find((i) => i.id === currentFilterId)}
                  activeFilter={activeFilters?.[currentFilterId]}
                  filterActions={filterActions}
                  clearSelected={clearSelectedForSecondaryFilter}
                  setSelectedFilter={setSelectedFilter}
                />
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      );
    };

    const renderSelectedFilter = () => {
      return filtersConfig.map((filter) => {
        const { component, id, modalActions, list } = filter || {};
        const FilterComponent = component;
        const isFilterActive = isFilterSelected(id, activeFilters);
        return (
          isFilterActive &&
          component && (
            <Box key={id}>
              <FilterComponent
                filterConfig={filtersConfig.find((i) => i.id === id)}
                modalActions={modalActions}
                filterActions={filterActions}
                activeFilters={activeFilters?.[id]}
                onAction={selectedFilter?.id === id ? clearSelected : () => {}}
                list={list || []}
                {...filter}
              />
            </Box>
          )
        );
      });
    };

    return (
      <Flex gridGap="3" justifyContent="space-between" w="100%">
        <Flex gap="10px" alignItems="center" flexWrap="wrap">
          <Box ref={ref}>{renderTrigger()}</Box>
          {renderSelectedFilter()}
          {showClear && (
            <KText
              variant="link"
              cursor="pointer"
              onClick={() => {
                filterActions({ type: 'clearAll' });
                setSelectedFilter('');
              }}
            >
              Clear all
            </KText>
          )}
        </Flex>
      </Flex>
    );
  }
);
